/**
 * Catálogo de configs — a espinha do hub de Administração (spec §3.1).
 *
 * Módulo TS estático, zero request — mesmo racional do catálogo de telas do
 * Spotlight (`use-screen-hits`): a busca responde antes de qualquer rede.
 * O `estadoAtual` NÃO vive aqui (é resolvido em runtime por `estado-resolver.ts`
 * sobre as queries existentes); aqui vivem só os metadados estáveis.
 *
 * Regra de construção (spec §3.4-5): frente nova de produto = ITEM novo neste
 * catálogo, nunca aba/página nova. Domínio passando de ~8 itens ⇒ TL arbitra.
 *
 * `capability` é o gate REAL do componente que implementa a config hoje —
 * não um mapeamento por conveniência. Dois itens divergiram do mapeamento
 * inicial na revisão de 22/Ago (vide task-1-report.md para o racional
 * completo de cada um):
 *  - `lifecycle`: `pages/TenantLifecyclePage.tsx` gateia por
 *    `tenant-lifecycle-read`, não `tenant-config-read`.
 *  - `integracoes`: `IntegrationsPage.tsx` gateia por `integrations-manage`
 *    OU `integrations-read` (papéis disjuntos — admin_tenant tem manage,
 *    dpo_tenant tem read; vide `role-mapping.ts`). O catálogo só aceita 1
 *    capability por item; escolhido `integrations-manage` para preservar a
 *    visibilidade do público primário do hub (admin_tenant). Efeito colateral
 *    aceito: dpo_tenant-only não vê este item no hub, mesmo tendo acesso
 *    read-only à página real.
 */
import { foldTerm } from '../../spotlight/fold-term';

export type DominioId = 'organizacao' | 'ia' | 'atendimento' | 'agenda' | 'integracoes' | 'conta';

export interface DominioDef {
  readonly id: DominioId;
  readonly label: string;
  readonly rota: string; // /admin/<id>
  readonly descricao: string;
}

export interface ConfigCatalogItem {
  /** Âncora estável — deep-link `/admin/<dominio>#<id>`. Nunca renomear. */
  readonly id: string;
  readonly label: string;
  /** «O que muda quando mexo» — 1 linha, exibida no item e na busca. */
  readonly efeito: string;
  readonly dominio: DominioId;
  /** Capability REAL que gateia a seção (mesma do componente que a implementa). */
  readonly capability: string;
  /** Rótulo de upsell quando locked (mesmo vocabulário do nav). */
  readonly tierLabel?: string;
  /** Sem backend ainda — aparece tracejado, não navega. */
  readonly soon?: boolean;
  /** Cap OFF ⇒ item AUSENTE (role-gated fail-closed), não locked. */
  readonly omitWhenBarred?: boolean;
  /** Item participa de cascata (Persona ▸ Playbook ▸ Tenant ▸ plataforma). */
  readonly temCascata?: boolean;
  /**
   * Natureza (spec 17/Set §4 · D3). Ausente = `config`: seção com âncora na página do
   * domínio, salva pela sticky bar. `'ferramenta'` = página própria montada dentro do
   * shell do /admin (`/admin/<dominio>/<id>`), dona do próprio ciclo de salvamento — um
   * editor de playbooks de 5.044 linhas não é campo de formulário. Ferramenta NÃO é
   * atalho para escapar do catálogo: declara `efeito`, `capability` real e `sinonimos`
   * como as seções; sem isso fica invisível na busca, que é exatamente o defeito que a
   * dobra das 15 páginas (§5, D5 30/Set/2026) existe para fechar.
   */
  readonly natureza?: 'ferramenta';
  readonly sinonimos: readonly string[];
}

export const DOMINIOS: readonly DominioDef[] = [
  {
    id: 'organizacao',
    label: 'Organização',
    rota: '/admin/organizacao',
    descricao: 'Quem somos: dados do tenant, identidade visual e pessoas.',
  },
  {
    id: 'ia',
    label: 'IA & conduta',
    rota: '/admin/ia',
    descricao: 'Como a IA fala, decide e quais modelos usa.',
  },
  {
    id: 'atendimento',
    label: 'Atendimento & fila',
    rota: '/admin/atendimento',
    descricao: 'Perfil de operação, fila e notificações do operador.',
  },
  {
    id: 'agenda',
    label: 'Agenda & lembretes',
    rota: '/admin/agenda',
    descricao: 'Agenda nativa e lembretes de agendamento.',
  },
  {
    id: 'integracoes',
    label: 'Integrações',
    rota: '/admin/integracoes',
    descricao: 'ERPs, CRMs e reconciliação de dados.',
  },
  {
    id: 'conta',
    label: 'Conta & plano',
    rota: '/admin/conta',
    descricao: 'Plano, capabilities do pacote e ciclo de vida do tenant.',
  },
];

export const CONFIG_CATALOG: readonly ConfigCatalogItem[] = [
  // ── Organização ────────────────────────────────────────────────────────
  {
    id: 'geral',
    label: 'Dados gerais',
    efeito: 'Nome de exibição e campos operacionais que aparecem nas conversas.',
    dominio: 'organizacao',
    capability: 'tenant-config-read',
    sinonimos: ['nome', 'dados do tenant', 'geral'],
  },
  {
    id: 'identidade-visual',
    label: 'Identidade visual',
    efeito: 'Logo e cores aplicados às superfícies do tenant.',
    dominio: 'organizacao',
    capability: 'tenant-config-read',
    tierLabel: 'Team+',
    sinonimos: ['branding', 'logo', 'cores', 'marca'],
  },
  // ── Organização · ferramentas (D5) ─────────────────────────────────────
  {
    id: 'importar-base',
    natureza: 'ferramenta',
    label: 'Importar base',
    efeito:
      'Carrega clientes por CSV para o registro do tenant — quem já existe é reconciliado, não duplicado.',
    dominio: 'organizacao',
    capability: 'customer-registry-import',
    tierLabel: 'Pro',
    sinonimos: ['csv', 'importar', 'clientes', 'base de clientes', 'cadastro'],
  },
  // ── IA & conduta ───────────────────────────────────────────────────────
  {
    id: 'conduta',
    label: 'Conduta da IA',
    efeito: 'Os 5 eixos de conduta no nível tenant — raiz da cascata Persona ▸ Playbook ▸ Tenant.',
    dominio: 'ia',
    capability: 'tenant-config-read',
    temCascata: true,
    sinonimos: ['tom', 'voz', 'estilo', 'personalidade'],
  },
  {
    id: 'confirmacao-cadastral',
    label: 'Confirmação cadastral automática',
    efeito: 'A IA envia a verificação com o titular sem esperar o clique do atendente.',
    dominio: 'ia',
    capability: 'tenant-config-read',
    sinonimos: ['cadastro', 'alteração cadastral', 'proatividade'],
  },
  {
    id: 'comportamental',
    label: 'Análise comportamental',
    // Fix round 1 (Admin Hub T8) — frase dobrada aqui do header local de
    // `BehavioralTab.tsx` que saiu ("sinaliza ao supervisor"): um lugar só
    // por descrição, em vez de duas.
    efeito: 'Liga a leitura de sinais comportamentais nas conversas e sinaliza ao supervisor.',
    dominio: 'ia',
    capability: 'tenant-config-read',
    sinonimos: ['comportamento', 'sentimento'],
  },
  {
    id: 'acompanhamento-silencioso',
    // Fix round 1 (Admin Hub T8) — o label/efeito ANTERIORES eram,
    // quase verbatim, a description da capability real `silent-monitoring`
    // (packages/shared/src/capabilities/capabilities.spec.yaml:544 — permissão
    // PER-USER, tier Team+, categoria coordination, ADR-094: supervisor observa
    // conversa sem sinalizar presença). Isso é OUTRO conceito de plataforma,
    // não tenant config — não tem nada a configurar neste hub. O componente
    // real por trás deste id (`SilentTab.tsx`, seu próprio docblock) é
    // `businessHours` + `outOfHoursMessage` via `OutOfHoursService` — horário
    // de atendimento por dia + resposta automática fora dele. Texto abaixo
    // descreve ISSO. `id`/`dominio` NÃO mudam (id é âncora + de-para da T14;
    // domínio é decisão arguível — vide task-8-report.md fix round 1 — e vai
    // para o TL como item, não se resolve aqui). Pinado em catalog.spec.ts.
    label: 'Horário de atendimento',
    efeito: 'Define os horários de atendimento por dia e a resposta automática enviada fora deles.',
    dominio: 'ia',
    capability: 'tenant-config-read',
    // 'silencioso'/'acompanhamento silencioso' ficam — quem procurava pelo
    // nome antigo da aba precisa continuar achando. 'espiar'/'supervisão'
    // SAEM: eram sinônimos do conceito ERRADO (silent-monitoring) e manteriam
    // a confusão viva na busca.
    sinonimos: [
      'silencioso',
      'acompanhamento silencioso',
      'horário',
      'expediente',
      'fora de horário',
      'auto-resposta',
    ],
  },
  {
    // Base de conhecimento do tenant (spec 2026-10-08 §3) — CRUD de `/v1/knowledge-entries`.
    // Cap de PAPEL (`knowledge-base-author`, só admin_tenant, `tier_min: solo`): quem não a
    // tem não está num plano inferior, logo `omitWhenBarred` (ausente, não teaser de tier).
    id: 'base-de-conhecimento',
    label: 'Base de conhecimento',
    efeito:
      'O que a IA sabe sobre o seu negócio (fatos que pode afirmar) e como deve agir (condutas).',
    dominio: 'ia',
    capability: 'knowledge-base-author',
    omitWhenBarred: true,
    sinonimos: [
      'conhecimento',
      'kb',
      'fatos',
      'conduta da ia',
      'regras',
      'palavras-chave',
      'faq',
      'perguntas frequentes',
    ],
  },
  {
    // IA como serviço S2 — carteira pré-paga pela ótica do tenant. Gate REAL da
    // seção é `ai-billing-read` (o mesmo do `AiWalletController` do engine); a
    // exportação CSV pede `ai-billing-manage` e é resolvida DENTRO da seção,
    // mesmo contrato que `modelos` usa para a sua cap de escrita.
    id: 'conta-saldo',
    label: 'Conta e saldo',
    efeito: 'Quanto de IA este tenant tem para gastar, a que preço, e para onde cada real foi.',
    dominio: 'ia',
    capability: 'ai-billing-read',
    // Sem `tierLabel`: `ai-billing-read` é `tier_min: solo` (capabilities.spec.yaml)
    // — quem não a tem não está num plano inferior, está num PAPEL sem ela.
    sinonimos: ['saldo', 'crédito', 'extrato', 'preço', 'contrato'],
  },
  {
    id: 'modelos',
    label: 'Modelos & failover',
    efeito: 'Qual modelo de IA atende cada agente e o que assume quando ele falha.',
    dominio: 'ia',
    capability: 'custom-model-selection',
    tierLabel: 'Pro',
    sinonimos: ['failover', 'reserva', 'fallback', 'provider', 'modelo'],
  },
  // ── IA & conduta · ferramentas (D5) ────────────────────────────────────
  {
    id: 'playbooks',
    natureza: 'ferramenta',
    label: 'Playbooks',
    efeito: 'A estratégia de cada conversa: etapas, checklists, transição e a persona que conduz.',
    dominio: 'ia',
    capability: 'playbook-read',
    sinonimos: ['playbook', 'estratégia', 'etapas', 'roteiro', 'checklist'],
  },
  {
    id: 'personas',
    natureza: 'ferramenta',
    label: 'Personas',
    efeito: 'A voz da IA: tom, fronteiras, o que ela nunca diz.',
    dominio: 'ia',
    capability: 'persona-read',
    sinonimos: ['persona', 'voz', 'tom', 'identidade da ia'],
  },
  {
    id: 'matriz-transicao',
    natureza: 'ferramenta',
    label: 'Matriz de transição',
    efeito: 'Quando a conversa pode passar de um playbook a outro, par a par.',
    dominio: 'ia',
    capability: 'playbook-read',
    sinonimos: ['transição', 'matriz', 'trocar de playbook'],
  },
  {
    // Cap opt-in fora de pacote (não chega por papel — vide nav antigo): sem ela o item
    // fica AUSENTE, não vira teaser de tier.
    id: 'risco-de-acao',
    natureza: 'ferramenta',
    label: 'Risco por Ação',
    efeito: 'Que categoria de ação exige verificar quem está falando antes de agir.',
    dominio: 'ia',
    capability: 'action-risk-category-read',
    omitWhenBarred: true,
    sinonimos: ['risco', 'ação', 'piso de identidade', 'verificar titular'],
  },
  {
    id: 'quick-replies',
    natureza: 'ferramenta',
    label: 'Quick replies',
    efeito: 'Atalhos de resposta por playbook — dependem da decisão 4 do Item 503.',
    dominio: 'ia',
    capability: 'playbook-read',
    sinonimos: ['resposta rápida', 'atalho', 'quick reply'],
  },
  {
    // §5/§11 da spec: a fusão Classificação + Gatilhos em UMA seção «Régua e gatilhos»
    // fica para depois (Item 555) — cada página tem hoje ciclo de salvamento próprio,
    // e dobrá-las na sticky bar é refactor das duas, não mudança de endereço.
    id: 'classificacao',
    natureza: 'ferramenta',
    label: 'Classificação',
    efeito: 'A régua canônica dos 4 níveis de sensibilidade que a plataforma aplica — leitura.',
    dominio: 'ia',
    capability: 'tenant-config-read',
    sinonimos: ['sensibilidade', 'régua', 'nível', 'classificar'],
  },
  {
    id: 'gatilhos',
    natureza: 'ferramenta',
    label: 'Gatilhos de proteção',
    efeito: 'O que a plataforma protege antes de agir — os mínimos de gatilho do tenant (ADR-148).',
    dominio: 'ia',
    capability: 'tenant-config-read',
    sinonimos: ['gatilho', 'proteção', 'trigger', 'mínimos'],
  },
  // ── Atendimento & fila ─────────────────────────────────────────────────
  {
    id: 'perfil-de-operacao',
    label: 'Perfil de operação',
    // Admin Hub T9 fix round 1 — texto trocado pela frase que vivia no
    // `<header>` próprio de `OperationProfileTab.tsx` (removido: dois
    // títulos empilhados dentro de uma `DomainSection`, mesma disciplina da
    // T8). Mais fiel à prancha 06 ("Um perfil aplica defaults coerentes de
    // privacidade e distribuição de fila...") do que o texto anterior
    // ("Preset que define quanto a IA conduz sozinha...").
    efeito:
      'Um perfil aplica defaults coerentes de privacidade e distribuição de fila — trocar de perfil é um evento auditado.',
    dominio: 'atendimento',
    capability: 'tenant-config-read',
    sinonimos: ['preset', 'modo de operação', 'autonomia'],
  },
  {
    id: 'fila-pos-transferencia',
    label: 'Fila pós-transferência',
    // Admin Hub T9 fix round 1 — dobrada a frase do `<header>` próprio de
    // `QueueLimboTab.tsx` (removido, mesma disciplina da T8) que dizia POR
    // QUE a política importa, não só o que ela é.
    efeito:
      'Política do que a IA faz com quem espera humano após a transferência — sem isto, mensagens do cliente na fila ficam sem retorno.',
    dominio: 'atendimento',
    capability: 'ai-queue-receptionist',
    omitWhenBarred: true,
    sinonimos: ['fila', 'limbo', 'espera', 'recepcionista'],
  },
  {
    id: 'notificacoes-do-operador',
    label: 'Notificações do operador',
    efeito: 'Janelas e turnos em que operadores recebem notificação.',
    dominio: 'atendimento',
    capability: 'tenant-config-read',
    sinonimos: ['turno', 'plantão', 'notificar'],
  },
  {
    id: 'notificacoes',
    label: 'Notificações',
    efeito: 'Canais e regras de notificação do tenant.',
    dominio: 'atendimento',
    capability: 'tenant-config-read',
    soon: true,
    sinonimos: ['alertas', 'avisos'],
  },
  // ── Atendimento & fila · ferramentas (D5) ──────────────────────────────
  {
    id: 'bonds',
    natureza: 'ferramenta',
    label: 'Bonds',
    efeito:
      'Como cada tipo de vínculo se comporta neste tenant — overlay sobre os 21 tipos canônicos.',
    dominio: 'atendimento',
    capability: 'bond-type-config-read',
    sinonimos: ['vínculo', 'bond', 'tipo de vínculo', 'relacionamento'],
  },
  {
    id: 'times-roteamento',
    natureza: 'ferramenta',
    label: 'Times & Roteamento',
    efeito: 'Quem atende o quê: times, regras de roteamento e distribuição da fila.',
    dominio: 'atendimento',
    capability: 'team-config-read',
    sinonimos: ['time', 'equipe', 'roteamento', 'distribuição', 'fila'],
  },
  // ── Agenda & lembretes ─────────────────────────────────────────────────
  {
    id: 'agenda-nativa',
    label: 'Agenda nativa',
    // Admin Hub T10 — dobrada a frase do `<header>` próprio de
    // `SchedulingTab.tsx` (removido, mesma disciplina de T7-T9) que dizia o
    // que o mockup não deixava óbvio: playbooks e clientes específicos podem
    // sobrepor este padrão (cascade Cliente ▸ Playbook ▸ Tenant ▸ plataforma).
    efeito:
      'A Lume gerencia horários e compromissos sem depender de ERP — playbooks e clientes específicos podem sobrepor este padrão.',
    dominio: 'agenda',
    capability: 'tenant-config-read',
    sinonimos: ['horários', 'agendamento', 'calendário'],
  },
  {
    id: 'lembretes',
    label: 'Lembretes de agendamento',
    // Admin Hub T10 — dobrada a frase do `<header>` próprio de
    // `RemindersTab.tsx` (removido) que descrevia o efeito de reagendar/
    // cancelar — mecânica não óbvia a partir do texto anterior.
    efeito:
      'Quando e como o cliente é lembrado de compromissos — reagendar move os lembretes; cancelar remove.',
    dominio: 'agenda',
    capability: 'tenant-config-read',
    sinonimos: ['lembrete', 'aviso de consulta', 'confirmação de horário'],
  },
  // ── Agenda & lembretes · ferramentas (D5) ──────────────────────────────
  {
    id: 'recursos',
    natureza: 'ferramenta',
    label: 'Recursos da agenda',
    efeito: 'Recursos, serviços e horários que a agenda nativa oferece para marcar.',
    dominio: 'agenda',
    capability: 'scheduling-native',
    sinonimos: ['recurso', 'serviço', 'sala', 'profissional', 'agenda nativa'],
  },
  {
    id: 'plantao',
    natureza: 'ferramenta',
    label: 'Escala de plantão',
    efeito: 'Quem está de plantão quando — o regime que libera notificação fora do turno.',
    dominio: 'agenda',
    capability: 'operator-oncall-schedule-read',
    sinonimos: ['plantão', 'escala', 'sobreaviso', 'on-call'],
  },
  // ── Integrações ────────────────────────────────────────────────────────
  {
    id: 'integracoes',
    label: 'Integrações',
    efeito: 'Conexões com ERPs/CRMs e reconciliação de dados.',
    dominio: 'integracoes',
    capability: 'integrations-manage',
    sinonimos: ['erp', 'crm', 'conector', 'superger'],
  },
  // ── Integrações · ferramentas (D5) ─────────────────────────────────────
  {
    id: 'templates-meta',
    natureza: 'ferramenta',
    label: 'Templates Meta (WhatsApp)',
    efeito:
      'Os modelos de mensagem aprovados pela Meta que o tenant pode disparar fora da janela de 24h.',
    dominio: 'integracoes',
    capability: 'message-template-read',
    sinonimos: ['template', 'hsm', 'whatsapp', 'modelo de mensagem', 'meta'],
  },
  // ── Conta & plano ──────────────────────────────────────────────────────
  {
    id: 'plano',
    label: 'Plano e billing',
    efeito: 'Plano contratado, uso e faturamento.',
    dominio: 'conta',
    capability: 'tenant-config-read',
    soon: true,
    sinonimos: ['fatura', 'cobrança', 'assinatura', 'billing'],
  },
  {
    id: 'capabilities',
    label: 'Pacote e capabilities',
    efeito: 'O que o pacote contratado liga e desliga na plataforma.',
    dominio: 'conta',
    capability: 'capability-registry-read',
    soon: true,
    sinonimos: ['pacote', 'recursos', 'features'],
  },
  {
    id: 'lifecycle',
    label: 'Ciclo de vida do tenant',
    efeito: 'Estado contratual do tenant e transições (ativação, churn).',
    dominio: 'conta',
    capability: 'tenant-lifecycle-read',
    sinonimos: ['lifecycle', 'churn', 'status do tenant'],
  },
];

/** Todos os itens do domínio, seções primeiro (a ordem do catálogo é a ordem de render). */
export function catalogByDominio(dominio: DominioId): ConfigCatalogItem[] {
  return CONFIG_CATALOG.filter((i) => i.dominio === dominio);
}

/** As ferramentas (D5): as 13 páginas que eram entradas planas de /configuracao/*. */
export const FERRAMENTAS: readonly ConfigCatalogItem[] = CONFIG_CATALOG.filter(
  (i) => i.natureza === 'ferramenta',
);

export function secoesByDominio(dominio: DominioId): ConfigCatalogItem[] {
  return CONFIG_CATALOG.filter((i) => i.dominio === dominio && i.natureza === undefined);
}

export function ferramentasByDominio(dominio: DominioId): ConfigCatalogItem[] {
  return CONFIG_CATALOG.filter((i) => i.dominio === dominio && i.natureza === 'ferramenta');
}

/**
 * Para onde um item leva — fonte ÚNICA para cards, busca do hub, bloco de ferramentas e
 * ⌘K. Antes vivia privado em `AdminHubPage.targetFor`; o ⌘K (T13) reimplementaria a regra.
 * `soon` nunca aponta para âncora sem seção (fix round 1 do hub, Finding 2).
 */
export function catalogTarget(item: ConfigCatalogItem): string {
  const dominio = DOMINIOS.find((d) => d.id === item.dominio)!;
  if (item.natureza === 'ferramenta') return `${dominio.rota}/${item.id}`;
  return item.soon === true ? dominio.rota : `${dominio.rota}#${item.id}`;
}

/** Busca local por label + sinônimos, com dobra de acento (reusa foldTerm do Spotlight). */
export function searchCatalog(term: string): ConfigCatalogItem[] {
  const folded = foldTerm(term);
  if (folded.length === 0) return [];
  return CONFIG_CATALOG.filter(
    (i) =>
      foldTerm(i.label).includes(folded) || i.sinonimos.some((s) => foldTerm(s).includes(folded)),
  );
}
